import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MUIButton from "@mui/material/Button";
import dayjs from "dayjs";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";
import CategoryModal from "@/ui/modals/categories/CategoryModal";
import {
  useCategoriesByTypes,
  useCategoryTypes,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "@/hooks/useCategories";
import {
  Category,
  CategoryType,
} from "@/domain/category.interface";
import { CategoryPayload } from "@/services/categories.service";
import { useAppTheme } from "@/utils/theme";
import { usePageStore } from "@/hooks/usePageStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useUserStore } from "@/hooks/useUserStore";
import { toTitleCase } from "@/utils/toTitleCase";
import { ErrorResponse } from "@/domain/error.interface";

const prettifyType = (t: CategoryType) =>
  t
    .split("_")
    .map((w) => toTitleCase(w))
    .join(" ");

const CategoriesPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const currentUserId = userInfo?._id;

  const [modalState, setModalState] = useState<{
    open: boolean;
    category?: Category;
    initialType?: CategoryType;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    category?: Category;
  }>({ open: false });

  const { categoryTypes, isLoadingCategoryTypes } = useCategoryTypes();
  const types = useMemo<CategoryType[]>(
    () => (categoryTypes as CategoryType[]) || [],
    [categoryTypes]
  );

  const {
    categoriesByType,
    isLoadingCategoriesByTypes,
    isFetchingCategoriesByTypes,
  } = useCategoriesByTypes(types);

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();

  useEffect(() => {
    setPageTitle("Categories");
  }, []);

  const handleOpenCreate = (type?: CategoryType) =>
    setModalState({ open: true, initialType: type });

  const handleOpenEdit = (category: Category) =>
    setModalState({ open: true, category });

  const handleCloseModal = () => setModalState({ open: false });

  const handleSave = async (data: CategoryPayload & { _id?: string }) => {
    const { _id, ...rest } = data;
    try {
      if (_id) {
        await updateCategory.mutateAsync({
          id: _id,
          data: { ...rest, updatedBy: currentUserId },
        });
        showSnackBar("The category has been updated.", "success");
      } else {
        await createCategory.mutateAsync({
          ...rest,
          createdBy: currentUserId,
        });
        showSnackBar("The category has been created.", "success");
      }
      handleCloseModal();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.category;
    if (!target) return;
    try {
      await deleteCategory.mutateAsync(target._id);
      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
        "error"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "type",
      isSortable: false,
      disablePadding: true,
      label: "Type",
      align: "left",
      width: "240px",
    },
    {
      id: "items",
      isSortable: false,
      disablePadding: false,
      label: "Items",
      align: "left",
      width: "120px",
    },
    {
      id: "status",
      isSortable: false,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "140px",
    },
    {
      id: "phoneNumber",
      isSortable: false,
      disablePadding: false,
      label: "Phone",
      align: "left",
      width: "180px",
    },
    {
      id: "createdAt",
      isSortable: false,
      disablePadding: false,
      label: "Created",
      align: "left",
      width: "160px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
      width: "100px",
    },
  ];

  const rows = useMemo(() => {
    if (!types.length) return [];

    return types.map((type, index) => {
      const category = categoriesByType.get(type) || null;
      const itemCount = category?.categories?.length || 0;

      const actionMenu = category
        ? [
            {
              value: "Edit",
              onClick: () => handleOpenEdit(category),
              icon: <EditIcon fontSize="small" />,
            },
            {
              value: "Delete",
              onClick: () => setDeleteAlert({ open: true, category }),
              icon: <DeleteOutlineIcon fontSize="small" />,
              sx: {
                color: theme.palette.error.main,
              },
            },
          ]
        : [
            {
              value: "Create",
              onClick: () => handleOpenCreate(type),
              icon: <AddIcon fontSize="small" />,
            },
          ];

      return createDataRow(index, {
        type: <div>{prettifyType(type)}</div>,
        items: (
          <Chip
            size="small"
            color={itemCount > 0 ? "primary" : "default"}
            label={
              <div className="text-xs">
                {`${itemCount} item${itemCount === 1 ? "" : "s"}`}
              </div>
            }
          />
        ),
        status: category ? (
          <Chip
            size="small"
            color={category.status === "ACTIVE" ? "success" : "default"}
            label={
              <div className="text-xs">
                {toTitleCase(category.status || "")}
              </div>
            }
          />
        ) : (
          <Typography variant="bodyMedium">-</Typography>
        ),
        phoneNumber: (
          <Typography variant="bodyMedium" component="div">
            {category?.phoneNumber || "-"}
          </Typography>
        ),
        createdAt: (
          <div>
            {category?.createdAt
              ? dayjs(category.createdAt).format("DD MMM YYYY")
              : "-"}
          </div>
        ),
        action: <ActionMenu id={type} options={actionMenu} />,
      });
    });
  }, [types, categoriesByType, theme]);

  const isModalPending =
    createCategory.isPending || updateCategory.isPending;

  const configuredTypes = useMemo(() => {
    const configured = new Set<CategoryType>();
    categoriesByType.forEach((cat, type) => {
      if (cat) configured.add(type);
    });
    return configured;
  }, [categoriesByType]);

  const availableTypesForCreate = useMemo(
    () => types.filter((t) => !configuredTypes.has(t)),
    [types, configuredTypes]
  );

  const isLoading =
    isLoadingCategoryTypes ||
    isLoadingCategoriesByTypes ||
    isFetchingCategoriesByTypes;

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Categories
                  </Typography>

                  <MUIButton
                    size="medium"
                    onClick={() => handleOpenCreate()}
                    variant="contained"
                    disabled={availableTypesForCreate.length === 0}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </MUIButton>
                </Box>

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order="asc"
                  orderBy="type"
                  rowsPerPage={types.length || 10}
                  page={0}
                  total={types.length}
                  isLoading={isLoading}
                  onChangePage={() => {}}
                  onChangeSize={() => {}}
                  onChangeSort={() => {}}
                  messageEmptyData="No categories to show."
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <CategoryModal
          open={modalState.open}
          onClose={handleCloseModal}
          category={modalState.category}
          initialType={modalState.initialType}
          availableTypes={
            modalState.category ? types : availableTypesForCreate
          }
          onSave={handleSave}
          isPending={isModalPending}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          title="Delete"
          description="Are you sure you want to delete this registry?"
          theme={theme}
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={deleteCategory.isPending}
        />
      )}
    </>
  );
};

export default CategoriesPage;
